'use client';

import { createContext, useCallback, useContext, useRef, useTransition, type ReactNode, type TransitionStartFunction } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
type ParamsMutation = (params: URLSearchParams) => void;
type ListingTransitionValue = {
  isPending: boolean;
  startTransition: TransitionStartFunction;
  /**
   * Apply a search-params mutation and push the new URL inside a React
   * transition so consumers can observe the pending state.
   */
  navigateWithParams: (mutate: ParamsMutation) => void;
  /**
   * Same mutation, but `router.replace(url, { scroll: false })` inside the
   * caller's own transition, so the shared `isPending` (grid → skeleton)
   * stays false. Used by the live search box.
   */
  replaceWithParams: (mutate: ParamsMutation, start: TransitionStartFunction) => void;
  /** A copy of the most recently intended params (may be ahead of the URL). */
  currentParams: () => URLSearchParams;
};
const ListingTransitionContext = createContext<ListingTransitionValue | null>(null);
export function ListingTransitionProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Every writer (search box, tiles, chips, filters) builds from the latest
  // *intended* params rather than the render's useSearchParams, which lags
  // while a navigation is pending — building from it would revert another
  // writer's change. `pending` holds query strings we navigated to that the
  // URL has not reached yet, so an intermediate commit does not roll the ref
  // back; any other URL change (back/forward, a link) re-seeds it.
  const seen = searchParams.toString();
  const intended = useRef(seen);
  const lastSeen = useRef(seen);
  const pending = useRef<string[]>([]);
  if (seen !== lastSeen.current) {
    lastSeen.current = seen;
    const i = pending.current.indexOf(seen);
    pending.current = i === -1 ? [] : pending.current.slice(i + 1);
    if (pending.current.length === 0) intended.current = seen;
  }
  const go = useCallback((mutate: ParamsMutation, start: TransitionStartFunction, method: 'push' | 'replace') => {
    const params = new URLSearchParams(intended.current);
    mutate(params);
    const qs = params.toString();
    intended.current = qs;
    if (qs !== lastSeen.current) pending.current.push(qs);
    const url = pathname + (qs ? `?${qs}` : '');
    start(() => {
      if (method === 'push') router.push(url);else router.replace(url, {
        scroll: false
      });
    });
  }, [pathname, router]);
  const navigateWithParams = useCallback((mutate_0: ParamsMutation) => go(mutate_0, startTransition, 'push'), [go]);
  const replaceWithParams = useCallback((mutate_1: ParamsMutation, start_0: TransitionStartFunction) => go(mutate_1, start_0, 'replace'), [go]);
  const currentParams = useCallback(() => new URLSearchParams(intended.current), []);
  return <ListingTransitionContext.Provider value={{
    isPending,
    startTransition,
    navigateWithParams,
    replaceWithParams,
    currentParams
  }}>
      {children}
    </ListingTransitionContext.Provider>;
}
export function useListingTransition() {
  const ctx = useContext(ListingTransitionContext);
  if (!ctx) {
    throw new Error("useListingTransition must be used within ListingTransitionProvider");
  }
  return ctx;
}